import {
  toWSDateString,
  WSCard,
  WSDataItem,
  WSFlexBox,
  WSGrid,
  WSInfoBox,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPill,
  WSText,
  WSTimeline
} from "@wingspanhq/fe-component-library";
import { BatchSummaryExpand } from "@wingspanhq/wingspan-api-sdk";
import { queriesBatches } from "@query";
import { useMemo } from "react";

import { WSQueries } from "../../../../query/WSQuery";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { getImportBatchTimelineItems } from "../../components/ImportBatchEventHistoryWidget/getImportBatchTimelineItems";
import { BULK_BATCH_EVENT_HISTORY_START_DATE } from "../../constants";
import { useImportBatchStatus } from "../../hooks/useImportBatchStatus";
import {
  useGoToImportPayablesBatches,
  routeHelpersImportPayablesBatch,
  routeHelpersImportPayablesBatchHistory
} from "../../paths";
import { selectorImportBatchName } from "../../selectors/selectorImportBatchName";
import {
  PAYABLE_ENTITY_LABEL,
  PAYABLE_ENTITY_LABEL_PLURAL
} from "../RouteImportPayableBatchDetails/constants";

export const RouteImportPayableBatchEventHistory: React.FC = () => {
  useBrowserPageTitle("Bulk imports: Payable batch history");
  const { params } = routeHelpersImportPayablesBatchHistory.useData();
  const { batchId } = params;

  const queryBatch = queriesBatches.getBatch.useQuery({ batchId });
  const querySummary = queriesBatches.getBatchSummary.useQuery({
    batchId,
    expand: [BatchSummaryExpand.BREAKDOWN]
  });
  const { pill, secondaryText } = useImportBatchStatus(batchId, true);

  const goToPayableBatches = useGoToImportPayablesBatches();
  const goToBatchDetails = routeHelpersImportPayablesBatch.useGoTo();

  const progress = querySummary.data?.progress;
  const batch = queryBatch.data;

  const timelineItems = useMemo(() => {
    if (!batch) {
      return [];
    }

    return getImportBatchTimelineItems({
      batch,
      itemCount: progress?.total,
      completedCount: progress?.completed,
      failedCount: progress?.failed,
      entityLabel: PAYABLE_ENTITY_LABEL,
      entityLabelPlural: PAYABLE_ENTITY_LABEL_PLURAL,
      showDetailedContent: true,
      onSeeMore: () => undefined
    });
  }, [batch, progress]);

  return (
    <WSQueries queries={{ queryBatch }}>
      {({ queryBatchData: loadedBatch }) => (
        <WSPage>
          <WSPageToolbar
            mb="XL"
            breadcrumbs={[
              {
                label: "Bulk imports (payables)",
                onClick: () => goToPayableBatches()
              },
              {
                label: selectorImportBatchName(loadedBatch),
                onClick: () => goToBatchDetails({ batchId })
              },
              { label: "Batch history" }
            ]}
          />

          <WSText.Heading3 weight="medium" color="gray700" mb="2XL">
            Batch history
          </WSText.Heading3>

          <WSGrid>
            <WSGrid.Item span={{ s: "12", m: "8" }}>
              <WSCard mb="L">
                <WSList.Horizontal equalItems>
                  <WSDataItem
                    label="Name"
                    value={selectorImportBatchName(loadedBatch)}
                  />
                  <WSFlexBox direction="column">
                    <WSText.ParagraphSm weight="book" color="gray500" mb="XS">
                      Status
                    </WSText.ParagraphSm>
                    <WSPill {...pill} />
                    {secondaryText && (
                      <WSText.ParagraphXs weight="book" color="gray500">
                        {secondaryText}
                      </WSText.ParagraphXs>
                    )}
                  </WSFlexBox>
                </WSList.Horizontal>
              </WSCard>

              <WSCard header={{ label: "History" }} divider>
                <WSList gap="L">
                  {timelineItems.length === 0 ? (
                    <WSText.ParagraphXs
                      weight="book"
                      color="gray400"
                      align="center"
                    >
                      No history events available
                    </WSText.ParagraphXs>
                  ) : (
                    <>
                      <WSTimeline items={timelineItems} />
                      <WSText.ParagraphXs
                        weight="book"
                        color="gray400"
                        align="center"
                      >
                        All available history events shown
                      </WSText.ParagraphXs>
                    </>
                  )}
                  <WSInfoBox>
                    <WSText.ParagraphXs weight="book" color="gray500">
                      Note: Bulk payable history events are only available from
                      the start date of the history feature:{" "}
                      {toWSDateString(
                        new Date(BULK_BATCH_EVENT_HISTORY_START_DATE),
                        "monthDayYear"
                      )}
                      .
                    </WSText.ParagraphXs>
                  </WSInfoBox>
                </WSList>
              </WSCard>
            </WSGrid.Item>
          </WSGrid>
        </WSPage>
      )}
    </WSQueries>
  );
};
